import type { AxiosInstance } from 'axios';
import type { Engine, PromptJob, ShotItem } from './store';
import { extractErrorMessage } from './api';
import { stripCharacterUgcImages, validateCharacterPortrait, type CharacterUgcRole } from './characterUgc';

export type RpcEngine = 'flowkit' | 'flowboard';
export type RpcApi = Pick<AxiosInstance, 'get' | 'post'>;
export interface PollOptions {
  intervalMs?: number;
  maxPolls?: number;
  sleep?: (ms: number) => Promise<void>;
}
export interface ReferenceVideoInput {
  api: RpcApi;
  engine: RpcEngine;
  shot: ShotItem;
  orientation: 'VERTICAL' | 'HORIZONTAL';
  prompt: string;
  characterPrompt?: string;
  getJob: () => PromptJob;
  updateJob: (patch: Partial<PromptJob>) => void;
  updateShot: (patch: Partial<ShotItem>) => void;
  polling?: PollOptions;
}

const BATCH = '/ugc/flowboard-batch-rpc';
const MAX_REFS = 7;
const isUuid = (value: unknown): value is string => typeof value === 'string'
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

function requireUuid(value: unknown, label: string): string {
  if (!isUuid(value)) throw new Error(`${label} returned no valid UUID`);
  return value;
}

export function nearestOmniDuration(duration: number): 4 | 6 | 8 | 10 {
  if (!Number.isFinite(duration) || duration <= 0) throw new Error('Shot duration must be positive');
  return ([4, 6, 8, 10] as const).reduce<4 | 6 | 8 | 10>((nearest, candidate) =>
    Math.abs(candidate - duration) < Math.abs(nearest - duration) ? candidate : nearest, 8);
}

function cachedProducts(job: PromptJob, engine: RpcEngine): { ids: string[]; count?: number } {
  return engine === 'flowkit'
    ? { ids: job.fkVaMediaIds || [], count: job.fkReferenceCount }
    : { ids: job.batchRpcReferenceMediaIds || [], count: job.batchRpcReferenceCount };
}

export function hasReferenceInput(shot: ShotItem, job: PromptJob | undefined, engine: RpcEngine): boolean {
  if (shot.fkVideoRequestId || shot.rpcSubmissionPending) return true; // allow safe resume/diagnosis
  if (shot.imageDataUrls.length || job?.jobImageUrls?.length) return true;
  if (!job) return false;
  const own = cachedProducts(job, engine).ids;
  const other = cachedProducts(job, engine === 'flowkit' ? 'flowboard' : 'flowkit').ids;
  return [own, other].some(ids => ids.length > 0 && ids.every(isUuid));
}

export function generationEngine(shot: ShotItem, selected: Engine): Engine {
  const owner = shot.rpcRequestEngine || shot.engine;
  return (shot.fkVideoRequestId || shot.rpcSubmissionPending) && (owner === 'flowkit' || owner === 'flowboard')
    ? owner : selected;
}

export function referenceMergeContext(job: PromptJob, shots: ShotItem[]): { projectId: string; videoId: string } {
  const contexts = shots.map(shot => {
    const flowboard = (shot.rpcRequestEngine || shot.engine) === 'flowboard';
    return {
      projectId: requireUuid(flowboard ? job.batchRpcProjectId : job.fkProjectId, 'Merge project'),
      videoId: requireUuid(flowboard ? job.batchRpcVideoId : job.fkVideoId, 'Merge video'),
    };
  });
  const context = contexts[0];
  if (!context) throw new Error('No finished reference videos to merge');
  if (contexts.some(item => item.projectId !== context.projectId || item.videoId !== context.videoId)) {
    throw new Error('These shots belong to different video contexts. Merge each original video separately; no clips were omitted.');
  }
  return context;
}

const requestPath = (engine: RpcEngine, requestId: string) =>
  `${engine === 'flowkit' ? '/flowkit-gen' : BATCH}/requests/${encodeURIComponent(requestId)}`;

function completedUrl(data: { output_url?: unknown }): string {
  if (typeof data.output_url !== 'string' || !/^https?:\/\//i.test(data.output_url)) {
    throw new Error('Flow reported completion without a playable video URL; the saved request has not been resubmitted.');
  }
  return data.output_url;
}

export async function pollReferenceVideo(api: RpcApi, engine: RpcEngine, requestId: string, options: PollOptions = {}): Promise<string> {
  const { intervalMs = 15_000, maxPolls = 140, sleep = ms => new Promise(resolve => setTimeout(resolve, ms)) } = options;
  let lastError = '';
  for (let poll = 0; poll < maxPolls; poll++) {
    if (poll) await sleep(intervalMs);
    let data;
    try {
      ({ data } = await api.get(requestPath(engine, requestId)));
    } catch (cause) {
      const status = (cause as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403 || status === 404) throw new Error(extractErrorMessage(cause));
      lastError = extractErrorMessage(cause);
      continue;
    }
    if (data?.status === 'COMPLETED') return completedUrl(data);
    if (data?.status === 'FAILED') throw new Error(extractErrorMessage(data.error_message, 'Video generation failed'));
  }
  throw new Error(`Video is still pending after the polling window. Resume this saved request rather than submitting again.${lastError ? ` Last poll: ${lastError}` : ''}`);
}

async function validateCharacterShot(input: ReferenceVideoInput, job: PromptJob): Promise<{
  roles: CharacterUgcRole[]; requiredRoles: CharacterUgcRole[]; maxVisible: number;
} | undefined> {
  if (!job.characterUgc) {
    if (input.shot.characterUgcVersion) throw new Error('Character UGC: this shot is missing its saved couple configuration.');
    return undefined;
  }
  if (job.ugcMode !== 'character' || job.duration !== 32 || !job.script
    || input.shot.characterUgcVersion !== 'couple-ugc-v1' || input.shot.durationSec !== 8) {
    throw new Error('Character UGC: keep the original 32-second job and its validated 8-second clips.');
  }
  const { data } = await input.api.post('/script/shots', {
    script: job.script, defaultDuration: 8, ugcMode: 'character', characterUgc: stripCharacterUgcImages(job.characterUgc),
  });
  const compiled = Array.isArray(data?.shots) && data.shots.length === 4
    ? data.shots.find((shot: ShotItem) => shot.shotNumber === input.shot.shotNumber) as ShotItem | undefined : undefined;
  if (!compiled || compiled.characterUgcVersion !== 'couple-ugc-v1' || compiled.durationSec !== 8
    || compiled.prompt !== input.shot.prompt || input.prompt.trim() !== compiled.prompt.trim()
    || compiled.voiceover !== '' || input.shot.voiceover !== '' || !Array.isArray(compiled.characterRoles)
    || compiled.characterRoles.some(role => role !== 'wife' && role !== 'husband')
    || JSON.stringify(compiled.characterRoles) !== JSON.stringify(input.shot.characterRoles)) {
    throw new Error('Character UGC: shot no longer matches the validated script. Queue a new corrected script before rendering.');
  }
  const allShots = data.shots as ShotItem[];
  if (allShots.some(shot => !Array.isArray(shot.characterRoles)
    || shot.characterRoles.some(role => role !== 'wife' && role !== 'husband'))) {
    throw new Error('Character UGC: incomplete character reference plan.');
  }
  return { roles: compiled.characterRoles, requiredRoles: [...new Set(allShots.flatMap(shot => shot.characterRoles!))],
    maxVisible: Math.max(...allShots.map(shot => shot.characterRoles!.length)) };
}

export async function generateReferenceVideo(input: ReferenceVideoInput): Promise<string> {
  const { api, engine, shot, orientation, updateJob, updateShot, getJob, polling } = input;
  const fk = engine === 'flowkit';
  const previousEngine = shot.rpcRequestEngine || shot.engine;
  if (shot.rpcSubmissionPending && !shot.fkVideoRequestId) {
    throw new Error('Submission outcome is unknown. Check request activity before starting another render.');
  }
  // Retry means resume accepted work. Only a confirmed pre-submit failure can
  // start a new attempt; a saved provider operation must never be paid for twice.
  if (shot.fkVideoRequestId) {
    if (previousEngine !== engine) throw new Error('Resume this request with its original engine before switching engines.');
    const { data } = await api.get(requestPath(engine, shot.fkVideoRequestId));
    if (data?.status === 'COMPLETED') return completedUrl(data);
    if (data?.status !== 'FAILED') return pollReferenceVideo(api, engine, shot.fkVideoRequestId, polling);
    const error = extractErrorMessage(data.error_message);
    if (data.request_id || error.includes('SUBMISSION_OUTCOME_UNKNOWN')) {
      throw new Error(`${error}. The provider operation needs recovery; no new render was submitted.`);
    }
    updateShot({ fkVideoRequestId: undefined, fkSceneId: undefined, rpcSubmissionPending: false });
  }

  const durationS = nearestOmniDuration(shot.durationSec);
  if (!input.prompt.trim()) throw new Error('A video prompt is required');
  let job = getJob();
  const couplePlan = await validateCharacterShot(input, job);
  const coupleRoles = couplePlan?.roles;
  const coupleConfig = job.characterUgc;
  const coupleBinding = JSON.stringify({ id: job.id, script: job.script, characters: coupleConfig?.characters.map(
    ({ role, name, appearance, voice, imageSha256 }) => ({ role, name, appearance, voice, imageSha256 })) });
  const assertCoupleUnchanged = () => {
    if (!coupleConfig) return;
    const current = getJob();
    if (coupleBinding !== JSON.stringify({ id: current.id, script: current.script, characters: current.characterUgc?.characters.map(
      ({ role, name, appearance, voice, imageSha256 }) => ({ role, name, appearance, voice, imageSha256 })) })) {
      throw new Error('Character UGC: job changed during generation preparation. No new video was submitted.');
    }
  };
  assertCoupleUnchanged();
  const ownProject = fk ? job.fkProjectId : job.batchRpcProjectId;
  const ownVideo = fk ? job.fkVideoId : job.batchRpcVideoId;
  const alternateProject = fk ? job.batchRpcProjectId : job.fkProjectId;
  const alternateVideo = fk ? job.batchRpcVideoId : job.fkVideoId;
  const portraitProject = isUuid(ownProject) && isUuid(ownVideo) ? ownProject
    : !ownVideo && isUuid(alternateProject) && isUuid(alternateVideo) && (!ownProject || ownProject === alternateProject)
      ? alternateProject : undefined;
  const portraitCache = job.characterUgcReferences?.projectId === portraitProject ? job.characterUgcReferences : undefined;
  const portraits = new Map<CharacterUgcRole, string>();
  const cachedPortraitIds = new Set<string>();
  for (const visibleRole of couplePlan?.requiredRoles || []) {
    const character = coupleConfig?.characters.find(character => character.role === visibleRole);
    if (!character?.imageSha256 || !/^[a-f0-9]{64}$/.test(character.imageSha256)) {
      throw new Error(`Character UGC: missing original ${visibleRole} portrait identity. Reattach its original reference.`);
    }
    const cached = portraitCache?.mediaByRole[visibleRole];
    if (cached && (!isUuid(cached.mediaId) || cached.imageSha256 !== character.imageSha256)) {
      throw new Error(`Character UGC: cached ${visibleRole} portrait does not match this job.`);
    }
    if (cached) {
      if (cachedPortraitIds.has(cached.mediaId)) throw new Error('Character UGC: both roles cannot share a cached portrait media ID.');
      cachedPortraitIds.add(cached.mediaId);
    }
    if (character.imageUrl) {
      const portrait = await validateCharacterPortrait(character.imageUrl);
      if (portrait.imageSha256 !== character.imageSha256) throw new Error(`Character UGC: ${visibleRole} portrait changed. Reattach the original.`);
      portraits.set(visibleRole, portrait.imageUrl);
    } else if (!cached) {
      throw new Error(`Character UGC: reattach the original ${visibleRole} portrait before generating.`);
    }
  }
  const images = job.jobImageUrls?.length ? job.jobImageUrls : shot.imageDataUrls;
  const files = job.jobImageFiles?.length ? job.jobImageFiles : shot.imageFiles;
  if (job.jobImageUrls?.length && shot.imageDataUrls.length
      && JSON.stringify(job.jobImageUrls) !== JSON.stringify(shot.imageDataUrls)) {
    throw new Error('Shot reference images differ from the shared job references. Use the original job images or start a new job.');
  }
  let cache = cachedProducts(job, engine);
  const otherEngine = fk ? 'flowboard' : 'flowkit';
  const otherCache = cachedProducts(job, otherEngine);
  const characterMode = job.ugcMode === 'character' && !coupleConfig;
  let charMediaId = characterMode ? (fk ? job.fkCharMediaId : job.batchRpcCharacterMediaId) : undefined;
  const charImage = characterMode ? job.characterImageUrl : undefined;
  const recordedCount = cache.count || otherCache.count;
  const expected = images.length || recordedCount || cache.ids.length || otherCache.ids.length;
  if (expected + (couplePlan?.maxVisible ?? (charMediaId || charImage ? 1 : 0)) > MAX_REFS) {
    throw new Error('Omni supports at most 7 reference images, including the character. No inputs were dropped.');
  }
  if (!expected) throw new Error('Attach the product reference images before generating');
  if (images.length && recordedCount && recordedCount !== images.length) {
    throw new Error('Job reference count changed. Keep the original job references or start a new job.');
  }
  if (coupleConfig) {
    for (const cached of [
      { ...cache, projectId: fk ? job.fkProjectId : job.batchRpcProjectId, videoId: fk ? job.fkVideoId : job.batchRpcVideoId },
      { ...otherCache, projectId: fk ? job.batchRpcProjectId : job.fkProjectId, videoId: fk ? job.batchRpcVideoId : job.fkVideoId },
    ]) {
      if ((cached.projectId && !isUuid(cached.projectId)) || (cached.videoId && !isUuid(cached.videoId))
        || (cached.ids.length && (!isUuid(cached.projectId) || !isUuid(cached.videoId)))
        || !cached.ids.every(isUuid) || new Set(cached.ids).size !== cached.ids.length
        || cached.ids.some(mediaId => cachedPortraitIds.has(mediaId))
        || (cached.count !== undefined && (!Number.isInteger(cached.count) || cached.count < 1 || cached.count !== expected))) {
        throw new Error('Character UGC: cached product references are inconsistent. Keep the original job context and references.');
      }
    }
    const reusable = (fk ? job.batchRpcProjectId : job.fkProjectId) === portraitProject
      && otherCache.ids.length > cache.ids.length && cache.ids.every((mediaId, index) => mediaId === otherCache.ids[index])
      ? otherCache : cache;
    if (reusable.ids.length > expected || (reusable.ids.length < expected && images.length !== expected)) {
      throw new Error('Character UGC: incomplete cached references. Reattach all original product images before generating.');
    }
  }
  if (images.length) {
    const hashes = await Promise.all(images.map(async image => {
      const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(image));
      return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
    }));
    if (job.rpcReferenceHashes && JSON.stringify(hashes) !== JSON.stringify(job.rpcReferenceHashes)) {
      throw new Error('Job reference images changed. Reattach the original references or start a new job.');
    }
    if (!job.rpcReferenceHashes) updateJob({ rpcReferenceHashes: hashes });
  }

  let projectId = fk ? job.fkProjectId : job.batchRpcProjectId;
  let videoId = fk ? job.fkVideoId : job.batchRpcVideoId;
  const otherProjectId = fk ? job.batchRpcProjectId : job.fkProjectId;
  const otherVideoId = fk ? job.batchRpcVideoId : job.fkVideoId;
  if (!videoId && isUuid(otherProjectId) && isUuid(otherVideoId) && (!projectId || projectId === otherProjectId)) {
    projectId = otherProjectId;
    videoId = otherVideoId;
    updateJob(fk ? { fkProjectId: projectId, fkVideoId: videoId } : { batchRpcProjectId: projectId, batchRpcVideoId: videoId });
  }
  if (!projectId || !videoId) {
    const title = job.idea?.slice(0, 60) || 'Master Prompt';
    const { data } = await api.post(fk ? '/flowkit-gen/ensure-project' : `${BATCH}/jobs`, fk
      ? { name: title, material: /anim|3d/i.test(job.material || '') ? '3d_pixar' : 'realistic', orientation }
      : { title, material: job.material, orientation });
    projectId = requireUuid(data?.projectId, 'Project creation');
    videoId = requireUuid(data?.videoId, 'Video creation');
    updateJob(fk ? { fkProjectId: projectId, fkVideoId: videoId } : { batchRpcProjectId: projectId, batchRpcVideoId: videoId });
  }
  requireUuid(projectId, 'Cached project');
  requireUuid(videoId, 'Cached video');
  assertCoupleUnchanged();

  // Both engines speak to the same Flow project. Reuse handles across engine
  // selections only when their project scope actually matches.
  if (otherProjectId === projectId && otherCache.ids.length > cache.ids.length
      && cache.ids.every((mid, index) => mid === otherCache.ids[index])) cache = otherCache;
  if (cache.count && cache.count !== expected) throw new Error('Job reference count changed; keep the original references.');
  let mediaIds = [...cache.ids];
  if (!mediaIds.every(isUuid)) throw new Error('Cached job contains an invalid reference media ID; reattach its references.');
  if (mediaIds.length > expected) throw new Error('Cached references do not match this job');
  if (mediaIds.length < expected && images.length !== expected) {
    throw new Error('Missing reference image bytes after an incomplete upload. Reattach all original job references to continue.');
  }
  const saveProducts = () => updateJob({
    ...((fk || otherProjectId === projectId) ? { fkVaMediaIds: [...mediaIds], fkReferenceCount: expected } : {}),
    ...((!fk || otherProjectId === projectId) ? { batchRpcReferenceMediaIds: [...mediaIds], batchRpcReferenceCount: expected } : {}),
  });
  if (images.length && !job.jobImageUrls?.length) updateJob({ jobImageUrls: [...images], jobImageFiles: [...files] });
  saveProducts(); // preserve expected count even if a later upload fails/reloads
  for (let index = mediaIds.length; index < expected; index++) {
    const name = `DDlab ${job.id} Product Ref ${index + 1}`;
    const { data } = await api.post(fk ? '/flowkit-gen/entities/ensure' : `${BATCH}/references`, fk
      ? { projectId, name, entityType: 'visual_asset', description: name,
          imageBase64: images[index], fileName: files[index] || `product-${index + 1}.png` }
      : { projectId, jobId: job.id, startIndex: index,
          references: [{ dataUrl: images[index], fileName: files[index] || `product-${index + 1}.png` }] },
    { timeout: 180_000 });
    mediaIds.push(requireUuid(fk ? data?.mediaId : data?.mediaIds?.[0], 'Reference upload'));
    // Persist each completed upload immediately, not only the final batch.
    saveProducts();
  }

  job = getJob();
  const portraitDirections: string[] = [];
  if (coupleConfig && coupleRoles?.length) {
    const saved = job.characterUgcReferences?.projectId === projectId ? job.characterUgcReferences.mediaByRole : {};
    const mediaByRole = { ...saved };
    const characterIds: string[] = [];
    for (const visibleRole of coupleRoles) {
      assertCoupleUnchanged();
      const character = coupleConfig.characters.find(character => character.role === visibleRole)!;
      let cached = mediaByRole[visibleRole];
      if (!cached) {
        const imageBase64 = portraits.get(visibleRole);
        if (!imageBase64) throw new Error(`Character UGC: reattach the original ${visibleRole} portrait for this project.`);
        const { data } = await api.post('/flowkit-gen/entities/ensure', {
          projectId, name: `DDlab ${job.id} Character ${visibleRole}`, entityType: 'character',
          description: `${character.name}: ${character.appearance}`, voiceDescription: character.voice,
          imageBase64, fileName: character.imageFile || `${visibleRole}.png`,
        }, { timeout: 180_000 });
        cached = { mediaId: requireUuid(data?.mediaId, `${visibleRole} portrait upload`), imageSha256: character.imageSha256! };
        mediaByRole[visibleRole] = cached;
        updateJob({ characterUgcReferences: { projectId, mediaByRole: { ...mediaByRole } } });
      }
      requireUuid(cached.mediaId, `${visibleRole} portrait`);
      if (cached.imageSha256 !== character.imageSha256 || characterIds.includes(cached.mediaId) || mediaIds.includes(cached.mediaId)) {
        throw new Error('Character UGC: portrait reference identity is inconsistent; no video was submitted.');
      }
      characterIds.push(cached.mediaId);
      portraitDirections.push(`Reference image ${characterIds.length} is ${character.name} (${visibleRole}); use this exact identity only during its scheduled visible intervals.`);
    }
    mediaIds = [...characterIds, ...mediaIds];
  }
  if (characterMode) {
    const otherCharacter = fk ? job.batchRpcCharacterMediaId : job.fkCharMediaId;
    if (!charMediaId && (fk ? job.batchRpcProjectId : job.fkProjectId) === projectId) charMediaId = otherCharacter;
    if (!charMediaId && charImage) {
      const { data } = await api.post('/flowkit-gen/entities/ensure', {
        projectId, name: `DDlab ${job.id} Character`, entityType: 'character',
        description: input.characterPrompt || job.characterDesc || job.characterName || 'Character reference',
        imageBase64: charImage, fileName: job.characterImageFile || 'character.png',
      }, { timeout: 180_000 });
      charMediaId = requireUuid(data?.mediaId, 'Character upload');
    }
    if (charMediaId) {
      requireUuid(charMediaId, 'Cached character');
      updateJob(fk ? { fkCharMediaId: charMediaId } : { batchRpcCharacterMediaId: charMediaId });
      mediaIds = [charMediaId, ...mediaIds];
    }
  }
  mediaIds = [...new Set(mediaIds)];
  if (mediaIds.length > MAX_REFS) throw new Error('Omni supports at most 7 reference images; no inputs were dropped.');
  assertCoupleUnchanged();
  const prompt = [...portraitDirections, characterMode ? input.characterPrompt : '', input.prompt].filter(Boolean).join('\n\n');
  let sceneId: string;
  let requestId: string;
  if (fk) {
    const { data } = await api.post('/flowkit-gen/scenes/scaffold', {
      videoId, displayOrder: shot.shotNumber, prompt,
      referenceMediaIds: mediaIds, narratorText: shot.voiceover,
    });
    sceneId = requireUuid(data?.sceneId, 'Scene creation');
    updateShot({ fkSceneId: sceneId });
  }
  assertCoupleUnchanged();
  updateShot({ rpcRequestEngine: engine, rpcSubmissionPending: true });
  try {
    if (fk) {
      const { data } = await api.post('/flowkit-gen/requests/batch', { requests: [{
        type: 'GENERATE_VIDEO_REFS', scene_id: sceneId!, project_id: projectId, video_id: videoId,
        orientation, model_family: 'omni_flash', duration_s: durationS,
      }] });
      requestId = requireUuid(data?.[0]?.id, 'Video submission');
    } else {
      const { data } = await api.post(`${BATCH}/scenes/reference-video`, {
        projectId, videoId, prompt, referenceMediaIds: mediaIds,
        orientation, durationS, displayOrder: shot.shotNumber, narratorText: shot.voiceover,
      });
      sceneId = requireUuid(data?.sceneId, 'Scene creation');
      requestId = requireUuid(data?.requestId, 'Video submission');
    }
  } catch (cause) {
    const status = (cause as { response?: { status?: number } })?.response?.status;
    // An explicit validation/auth rejection did not accept work. Network/5xx
    // failures remain uncertain and cannot be automatically submitted again.
    if (status && [400, 401, 403, 422].includes(status)) updateShot({ rpcSubmissionPending: false });
    throw cause;
  }
  updateShot({ fkSceneId: sceneId!, fkVideoRequestId: requestId, rpcSubmissionPending: false });
  return pollReferenceVideo(api, engine, requestId, polling);
}